// layout.tab-group.v1
//
// A flat (no border) horizontal tab bar with a pill-style active state.
// Designed for the chrome-level mode switcher (e.g. Glass Orders vs
// CRM records, or Image/Video/Lipsync in Generative
// Studio). Not for in-form / settings tabs — those should use a
// different visual.
//
// Stateless. Parent owns activeId and onChange.

import { For, type JSX, Show, splitProps } from "solid-js"
import { Icon, type IconName } from "../../components/icon"

export interface TabGroupItem<TId extends string = string> {
  id: TId
  label: string
  icon?: IconName
  disabled?: boolean
  count?: number
}

export interface TabGroupProps<TId extends string = string> {
  items: TabGroupItem<TId>[]
  activeId: TId
  onChange: (id: TId) => void
  ariaLabel?: string
  class?: string
}

export function TabGroup<TId extends string = string>(props: TabGroupProps<TId>) {
  const [local, rest] = splitProps(props, ["items", "activeId", "onChange", "ariaLabel", "class"])

  return (
    <div
      data-component="layout.tab-group"
      role="tablist"
      aria-label={local.ariaLabel}
      classList={{
        "inline-flex items-center gap-1 p-1 rounded-full bg-surface-raised-base border border-border-weaker-base": true,
        [local.class ?? ""]: !!local.class,
      }}
      {...rest}
    >
      <For each={local.items}>
        {(item) => {
          const active = () => item.id === local.activeId
          return (
            <button
              type="button"
              role="tab"
              aria-selected={active()}
              data-tab-id={item.id}
              data-active={active() ? "true" : undefined}
              disabled={item.disabled}
              onClick={() => local.onChange(item.id)}
              classList={{
                "inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-13-medium transition-colors": true,
                "bg-background-base text-text-strong shadow-xs-border": active(),
                "text-text-base hover:text-text-strong hover:bg-surface-raised-base-hover": !active(),
                "opacity-50 cursor-not-allowed": !!item.disabled,
              }}
            >
              <Show when={item.icon}>{(name) => <Icon name={name()} size="small" class="shrink-0" />}</Show>
              <span class="truncate">{item.label}</span>
              <Show when={typeof item.count === "number"}>
                <span class="ml-0.5 rounded-full bg-surface-raised-base-active px-1.5 py-0.5 text-11-medium text-text-base tabular-nums">
                  {item.count}
                </span>
              </Show>
            </button>
          )
        }}
      </For>
    </div>
  ) as JSX.Element
}
